import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

export const useLatestRequest = () => {
  const controllerRef = useRef<AbortController | null>(null);
  const [loading, setLoading] = useState(false);

  const cancel = useCallback(() => {
    const controller = controllerRef.current;
    controllerRef.current = null;
    controller?.abort();
    setLoading(false);
  }, []);

  const run = useCallback(
    async <T>(request: (signal: AbortSignal) => Promise<T>): Promise<T | undefined> => {
      controllerRef.current?.abort();

      const controller = new AbortController();
      controllerRef.current = controller;
      setLoading(true);

      try {
        return await request(controller.signal);
      } catch (error) {
        if (controller.signal.aborted || axios.isCancel(error)) return undefined;
        throw error;
      } finally {
        if (controllerRef.current === controller) {
          controllerRef.current = null;
          setLoading(false);
        }
      }
    },
    [],
  );

  useEffect(
    () => () => {
      const controller = controllerRef.current;
      controllerRef.current = null;
      controller?.abort();
    },
    [],
  );

  return { run, cancel, loading };
};
